import { FC, useCallback, useEffect, useState } from 'react';
import { Prompt } from 'react-router-dom';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  CircularProgress,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import { ArrowDownward, ArrowUpward, Close, ExpandMore, Warehouse } from '@mui/icons-material';

import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import PageHeader from '../../../sharedModules/layout/PageHeader';
import { AxiosResponse } from 'axios';
import axios from '../../../sharedModules/utils/axios.utils';

import { getWarehouseSelectedLocation } from '../../navigation/components/WarehouseLocationToggle';
import {
  WarehouseGroup,
  WarehouseListResponse,
  WarehouseOption,
  fetchWarehouseList,
  groupsWithFavorites,
} from '../model';

// Favorites are global per location, so this page edits shared configuration.
// Changes accumulate locally and commit as one PUT of the full ordered list —
// the endpoint replaces the whole set, so there is no per-row position to patch.
interface WarehouseFavoritesPayload {
  location: string;
  wh_ids: string[];
}

const WarehouseOptionsPage: FC = () => {
  const [location] = useState<string>(() => getWarehouseSelectedLocation());
  const [groups, setGroups] = useState<WarehouseGroup[]>([]);
  const [favorites, setFavorites] = useState<WarehouseOption[]>([]);
  const [dirty, setDirty] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const applyResponse = (data: WarehouseListResponse) => {
    setGroups(groupsWithFavorites(data));
    setFavorites(data.favorites);
    setDirty(false);
  };

  const loadWarehouses = useCallback(async () => {
    setLoading(true);
    try {
      applyResponse(await fetchWarehouseList(location));
      setErrorMessage('');
    } catch (error: any) {
      setErrorMessage(error?.response?.data?.error || 'Could not load warehouses.');
    } finally {
      setLoading(false);
    }
  }, [location]);

  useEffect(() => {
    loadWarehouses();
  }, [loadWarehouses]);

  const isFavorited = (whId: string) => favorites.some((favorite) => favorite.wh_id === whId);

  const toggleFavorite = (warehouse: WarehouseOption, group: string) => {
    setDirty(true);
    setFavorites((previous) =>
      previous.some((favorite) => favorite.wh_id === warehouse.wh_id)
        ? previous.filter((favorite) => favorite.wh_id !== warehouse.wh_id)
        : [...previous, { ...warehouse, group }],
    );
  };

  const move = (index: number, offset: number) => {
    const target = index + offset;
    if (target < 0 || target >= favorites.length) return;

    setDirty(true);
    setFavorites((previous) => {
      const next = [...previous];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // axios 0.22 types put<T>(url, data?: T), so the body must be the first generic
      // and the response type the second — otherwise the body is checked against the response.
      const response = await axios.put<WarehouseFavoritesPayload, AxiosResponse<WarehouseListResponse>>(
        '/receiving/warehouse_favorites',
        { location, wh_ids: favorites.map((favorite) => favorite.wh_id) },
      );
      applyResponse(response.data);
      setErrorMessage('');
    } catch (error: any) {
      setErrorMessage(error?.response?.data?.error || 'Could not save favorites.');
    } finally {
      setSaving(false);
    }
  };

  // Guard against losing unsaved ranking on a reload or tab close.
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (!dirty) return;
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  return (
    <>
      <Prompt when={dirty} message="You have unsaved favorite changes. Leave this page?" />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <PageHeader titleIcon={<Warehouse />} mainTitle="Warehouse Options" />
        </Grid>
        <Grid item xs={12}>
          <ErrorMessage message={errorMessage} />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body2" color="text.secondary">
            Favorites appear at the top of the warehouse dropdown on the Receiving page, in the order below. This
            configuration is shared by everyone at this location. Switch locations with the toggle in the top bar.
          </Typography>
        </Grid>
        {loading ? (
          <Grid item xs={12}>
            <CircularProgress />
          </Grid>
        ) : (
          <>
            <Grid item xs={12} md={7}>
              <Paper style={{ padding: 16 }}>
                <Typography variant="h6" style={{ marginBottom: 12 }}>
                  All Warehouses
                </Typography>
                {groups.map((group) => (
                  <Accordion
                    key={group.group}
                    disableGutters
                    defaultExpanded={false}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      overflow: 'hidden',
                      boxShadow: 1,
                      mb: 1,
                      '&:before': { display: 'none' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      sx={{
                        px: 2,
                        minHeight: 48,
                        '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1, my: 1 },
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        {group.group}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        · {group.warehouses.length}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0, px: 1 }}>
                      <List dense>
                        {group.warehouses.map((warehouse) => (
                          <ListItem key={warehouse.wh_id} disableGutters>
                            <Checkbox
                              checked={isFavorited(warehouse.wh_id)}
                              onChange={() => toggleFavorite(warehouse, group.group)}
                            />
                            <ListItemText primary={`${warehouse.name} (${warehouse.wh_id})`} />
                          </ListItem>
                        ))}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Paper>
            </Grid>
            <Grid item xs={12} md={5}>
              <Paper style={{ padding: 16 }}>
                <Typography variant="h6">Favorites</Typography>
                {favorites.length === 0 && (
                  <Typography variant="body2" color="text.secondary" style={{ marginTop: 8 }}>
                    No favorites yet. Check a warehouse on the left to add it here.
                  </Typography>
                )}
                <List dense>
                  {favorites.map((favorite, index) => (
                    <ListItem key={favorite.wh_id} disableGutters>
                      <ListItemText primary={`${index + 1}. ${favorite.name}`} secondary={favorite.group} />
                      <IconButton size="small" disabled={index === 0} onClick={() => move(index, -1)}>
                        <ArrowUpward fontSize="small" />
                      </IconButton>
                      <IconButton size="small" disabled={index === favorites.length - 1} onClick={() => move(index, 1)}>
                        <ArrowDownward fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => toggleFavorite(favorite, favorite.group || '')}>
                        <Close fontSize="small" />
                      </IconButton>
                    </ListItem>
                  ))}
                </List>
                <Button variant="contained" disabled={!dirty || saving} onClick={handleSave}>
                  {saving ? <CircularProgress size={24} /> : 'Save Favorites'}
                </Button>
              </Paper>
            </Grid>
          </>
        )}
      </Grid>
    </>
  );
};

export default WarehouseOptionsPage;
